import { useEffect, useRef, useState, type ReactNode } from "react"

import { Button, SearchInput, Skeleton } from "@coval/ui"
import { ArrowLeft, ChevronRight } from "lucide-react"

import type { SearchableItem } from "./SearchableCheckboxList"

interface MetricValuePickerProps {
  items: SearchableItem[]
  selectedId: string
  onSelect: (id: string) => void
  loading?: boolean
  children: ReactNode
}

export function MetricValuePicker({
  items,
  selectedId,
  onSelect,
  loading,
  children,
}: MetricValuePickerProps) {
  const [search, setSearch] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)
  const selected = items.find((item) => item.id === selectedId)
  const query = search.trim().toLowerCase()
  const matches = items.filter((item) =>
    (item.searchValue ?? item.label).toLowerCase().includes(query),
  )

  useEffect(() => {
    if (!selectedId) {
      searchRef.current?.focus()
    }
  }, [selectedId])

  if (selected) {
    return (
      <div className="space-y-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            icon
            variant="ghost"
            aria-label="Back to metrics"
            tooltip="Back to metrics"
            onClick={() => onSelect("")}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <span className="truncate text-sm font-medium" title={selected.label}>
            {selected.label}
          </span>
        </div>
        {children}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <SearchInput
        ref={searchRef}
        aria-label="Search metrics"
        placeholder="Search metrics…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onClear={() => setSearch("")}
      />
      <div className="max-h-64 overflow-y-auto" aria-label="Available metrics">
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : matches.length ? (
          matches.map((item) => (
            <Button
              key={item.id}
              variant="ghost"
              className="h-auto min-h-8 w-full justify-start"
              onClick={() => onSelect(item.id)}
            >
              <span className="min-w-0 flex-1 whitespace-normal break-words text-left">
                {item.label}
              </span>
              <ChevronRight className="size-4 shrink-0" />
            </Button>
          ))
        ) : (
          <p className="py-4 text-center text-sm text-coval-text-muted">
            {query ? "No matching metrics" : "No workspace metrics"}
          </p>
        )}
      </div>
    </div>
  )
}
